import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import Button from 'components/Button';
import { Grid } from '@mui/material';
import AlertDialog from 'components/AlertDialog';
import TextField from 'components/FormElements/AppTextInput';
import GridContainer from 'components/GridContainer';
import PageContainer from 'components/PageContainer';
import Table from 'components/Table';
import { useDisclosure, useFilters, usePagination } from 'further-ui/hooks';
import {
  numberToCurrencyString,
  numberToDisplayString,
} from 'further-ui/utils';
import { makeStyles } from 'tss-react/mui';

import { ORGANISATION_ADD } from 'adminConstants/routes';
import PageContent from 'components/PageContent';
import TooltipTotalValue from 'components/TooltipTotalValue';
import api from 'lib/trpcClient';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { useGetRole } from 'hooks/ui/useGetRole';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import TableActions from './TableActions';
import { useBackgroundExports } from 'hooks/data/backgroundExports/useBackgroundExports';
import { BackgroundExportTypes } from 'further-types/background-exports';

type Filters = {
  search?: string;
};

const useStyles = makeStyles()((theme) => ({
  actionButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'flex-start',
    },
  },
  tableMt: {
    marginTop: 10,
  },
  marginLeft: {
    marginLeft: 10,
  },
  exportSpinner: {
    color: 'rgba(0, 0, 0, 0.26)',
    marginRight: 10,
  },
}));

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Organisations', isActive: true },
];

const Organisation: React.FC = () => {
  const { classes } = useStyles();
  const { firmId } = useGetRole();

  const pagination = usePagination({
    id: 'organisations',
    order: 'desc',
    orderBy: 'createdAt',
  });
  const { filters, handleChangeFilters } = useFilters<Filters>(
    'organisations',
    { onFiltersChange: pagination.toFirstPage },
  );

  const [search, setSearch] = useState(filters?.search || '');
  const debouncedSearch = useDebounce(search, 250);
  useEffect(() => {
    handleChangeFilters({ search: debouncedSearch });
  }, [debouncedSearch]);
  const params = {
    search: filters.search,
    firmId,
    order: pagination.order,
    orderBy: pagination.orderBy,
    includeInvestmentValues: true,
  };
  const { data, isFetching } = api.organisation.list.useQuery({
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    ...params,
  });

  const { mutate: deleteOrganisation, isPending: isDeletingOrganisation } =
    api.organisation.deleteById.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Organisation deleted successfully.',
      }),
    );

  const [createPermission] = useHasPermissions([
    RolePermission.CreateOrganisations,
  ]);
  const deleteDisclosure = useDisclosure();

  const columns = [
    { label: 'Organisation name', key: 'name' },
    {
      label: 'Parent organisation',
      key: 'parentId.name',
      sort: false,
      render: (elm) => elm?.parentId?.name ?? '-',
    },
    {
      label: 'Advisers',
      key: 'linkedAdvisersCount',
      sort: false,
      render: (elm) => numberToDisplayString(elm?.linkedAdvisersCount),
    },
    {
      label: 'Clients',
      key: 'investorsCount',
      sort: false,
      render: (elm) => numberToDisplayString(elm?.investorsCount),
    },
    {
      label: 'Subscriptions',
      key: 'investmentsCount',
      sort: false,
      render: (elm) => numberToDisplayString(elm?.investmentsCount),
    },
    {
      label: 'Subscription value',
      key: 'initialValue',
      sort: false,
      render: (elm) => numberToCurrencyString(elm?.initialValue),
    },
    {
      label: <TooltipTotalValue />,
      key: 'currentValue',
      sort: false,
      render: (elm) => numberToCurrencyString(elm?.currentValue),
    },
    {
      label: 'Realised value',
      key: 'realisedValue',
      sort: false,
      render: (elm) => numberToCurrencyString(elm?.realisedValue),
    },
    {
      label: 'Advice fees',
      key: 'adviceFees',
      sort: false,
      render: (elm) => numberToCurrencyString(elm?.adviceFees),
    },
    {
      label: 'Actions',
      key: '',
      sort: false,
      render: (row) => (
        <TableActions
          useOrganisaionSpecificEditPermissions={Boolean(firmId)}
          row={row}
          deleteDisclosure={deleteDisclosure}
        />
      ),
    },
  ];

  const onDeleteOrganisation = async () => {
    const organisationId = deleteDisclosure.actionProps.id;
    deleteOrganisation({ id: organisationId });
    deleteDisclosure.onClose();
  };

  const { createBackgroundExport, isExporting } = useBackgroundExports();

  const handleExport = () => {
    createBackgroundExport.mutate({
      exportType: BackgroundExportTypes.OrganisationsExport,
      firmId: params.firmId,
      params: {
        search: debouncedSearch,
        order: pagination.order,
        orderBy: pagination.orderBy,
      },
    });
  };

  return (
    <PageContainer heading="Organisations" breadcrumbs={breadcrumbs}>
      <PageContent>
        <GridContainer>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <TextField
              type="search"
              name="search"
              placeholder="Search by user email, name, FCA number"
              fullWidth
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 8,
            }}
          >
            <div className={classes.actionButtonContainer}>
              {createPermission && (
                <Link to={ORGANISATION_ADD}>
                  <Button color="primary" variant="contained">
                    Add Organisation
                  </Button>
                </Link>
              )}
              <Button
                color="primary"
                variant="contained"
                className={classes.marginLeft}
                loading={isExporting}
                onClick={handleExport}
              >
                Export
              </Button>
            </div>
          </Grid>
        </GridContainer>

        <div className={classes.tableMt}>
          <Table
            columns={columns}
            onRequestSort={pagination.handleRequestSort}
            order={pagination.order}
            orderBy={pagination.orderBy}
            tablebody={data?.organisation ?? []}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            count={data?.totalOrganisation ?? 0}
            pagination={true}
            loading={isFetching}
            variant="nohover"
          />
        </div>
      </PageContent>
      <AlertDialog
        onClose={deleteDisclosure.onClose}
        open={deleteDisclosure.isOpen}
        title={
          deleteDisclosure.actionProps.hasLinks
            ? 'Active organisation'
            : 'Are you sure you wish to delete this organisation?'
        }
        content={
          deleteDisclosure.actionProps.hasLinks
            ? 'If you delete this organisation, its linked advisers (and child organisations, if applicable) will be disconnected, and organisation-level users will have their access removed. This cannot be undone.'
            : 'This cannot be undone and all information relating to the organisation will be lost.'
        }
        btnLabels={{ confirm: 'Delete organisation', cancel: 'Go back' }}
        cancelBtnProps={{ variant: 'contained', color: 'primary' }}
        confirmBtnProps={{
          variant: 'outlined',
          color: 'primary',
          disabled: isDeletingOrganisation,
        }}
        onConfirm={onDeleteOrganisation}
      />
    </PageContainer>
  );
};

export default Organisation;
